import { Link, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { useFlag } from '@/hooks/useFlags';
import { QUICKBOOKS_PATH } from './paths';
import { useQuickbooksStatus } from './hooks';

/**
 * A QuickBooks connection that ran out (Intuit's mandatory "reconnect" notice), above every page of the app for the owner. Dark behind the
 * `quickbooks` flag: for anyone else, or while the flag is off, it asks nothing. One status request per load (not on every window focus),
 * and it points to the QuickBooks page, whose official button does the reconnecting. The page shows its own banner, so this stays out of it.
 */
export function ReconnectBanner() {
  const { t } = useTranslation('quickbooks');
  const { user } = useAuth();
  const [location] = useLocation();
  const flag = useFlag('quickbooks');
  const owner = user?.role === 'admin';
  const { data } = useQuickbooksStatus(owner && flag, { staleTime: 5 * 60_000, refetchOnWindowFocus: false, retry: false });

  if (!owner || !flag || location.startsWith(QUICKBOOKS_PATH)) return null;
  const c = data?.connection;
  if (!c || c.status === 'disconnected' || !c.needs_reconnect) return null;
  return (
    <div role="alert" className="flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-900 sm:flex-row sm:items-center sm:gap-3">
      <AlertCircle className="hidden h-4 w-4 flex-shrink-0 sm:block" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-sm">{t('reconnect.bannerText')}</p>
      <Link href={QUICKBOOKS_PATH}>
        <Button size="sm" variant="outline" className="whitespace-nowrap bg-white">{t('reconnect.bannerAction')}</Button>
      </Link>
    </div>
  );
}
